import { Box, Button, Heading, SidePanel } from '@wix/design-system';
import { SparklesFilled } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import React, { type CSSProperties, useContext, useRef } from 'react';

import { EditorContext, PluginsEventsContext, RicosContext } from './../../ricos-context';
import type { TFunction } from './../../ricos-types';
import {
  Panel,
  PanelHeader,
  useClickOutside,
  usePanelContainerElement,
} from './../../ui-components';

import {
  AI_MODAL_ERROR_CANCEL_BUTTON,
  AI_MODAL_ERROR_RETRY_BUTTON,
  AI_MODAL_LOADING_INDICATOR,
  AI_MODAL_RESULTS_CONTENT,
  AI_MODAL_ROOT,
} from '../data-hooks';
import type { DiscardPayload } from '../discard-confirmation-modal/discard-confirmation-modal';
import { ErrorMessage } from '../error-message/error-message';
import { unreachable } from '../ts-utils';
import type {
  AiPluginEditorConfig,
  InitialPromptOptions,
  Prompt,
  ReferenceElement,
} from '../types';
import { AI_MODAL_ID, AI_TYPE } from '../types';
import { getPromptBiValue } from '../utils/get-prompt-bi';
import { getSelectionAsHTML } from '../utils/get-selection-as-html';
import styles from './ai-content-modal-scss';
import { Disclaimer } from './disclaimer/disclaimer';
import { Feed } from './feed/feed';
import { NoSelectionMessage } from './no-selection-message/no-selection-message';
import { PromptsList } from './prompts-list';
import {
  TextualPromptInputArea,
  type TextualPromptInputAreaRef,
} from './textual-prompt-input-area/textual-prompt-input-area';
import { useAiModalApi } from './use-ai-modal-api';

type AiContentModalProps = {
  closeModal: () => void;
  openDiscardModal: (referenceElement: ReferenceElement) => Promise<DiscardPayload>;
  config: AiPluginEditorConfig;
  initialPromptOptions?: InitialPromptOptions;
  feedUserEntryFormatter?: (prompt: string | Prompt, t: TFunction) => string;
  zIndex?: CSSProperties['zIndex'];
  modalSessionId: string;
};
const PROMPT_CHAR_LIMIT = 500;

export const AiContentModal: React.FC<AiContentModalProps> = ({
  closeModal,
  openDiscardModal,
  config,
  zIndex,
  modalSessionId,
  initialPromptOptions,
  feedUserEntryFormatter,
}) => {
  const { t } = useContext(RicosContext);
  const { adapter } = useContext(EditorContext);
  const inputRef = React.useRef<TextualPromptInputAreaRef>(null);
  const containerEl = usePanelContainerElement();
  const listRef = useRef<HTMLDivElement>(null);
  const selection = React.useMemo(() => getSelectionAsHTML(adapter.tiptapEditor), []);
  const resolvedZIndex = typeof zIndex === 'number' ? zIndex : 5500;

  const pluginsEvents = useContext(PluginsEventsContext);

  const publishModalAction = React.useMemo(() => {
    let counter = 0;
    return (actionName: string, value?: string) => {
      counter++;
      pluginsEvents.publishModalAction({
        pluginId: AI_TYPE,
        modalId: AI_MODAL_ID,
        modalSessionId,
        actionName,
        value,
        actionNumber: counter,
      });
    };
  }, [pluginsEvents.publishModalAction, modalSessionId]);

  const {
    submitPrompt,
    setFreePromptValue,
    setSelectedPromptIndex,
    triggerFreePrompt,
    cancelPrompt,
    retryPrompt,
    goBack,
    textualPrompt,
    promptsList,
    state,
    getResultActions,
    tryCloseModal,
    isDiscardModalOpen,
  } = useAiModalApi({
    selection: selection || '',
    ...config,
    closeModal,
    openDiscardModal,
    modalRef: { current: containerEl },
    inputRef,
    listRef,
    publishModalAction,
    initialPromptOptions,
  });

  const closeOnClickOutside = React.useCallback(() => {
    tryCloseModal();
    publishModalAction('close_on_click_outside');
  }, [tryCloseModal]);

  useClickOutside({
    containerRef: { current: containerEl },
    onClickOutside: closeOnClickOutside,
    disable: isDiscardModalOpen,
  });

  React.useEffect(() => {
    if (initialPromptOptions?.autoTrigger && state.type === 'INITIAL') {
      submitPrompt(initialPromptOptions.prompt, selection);
    }
  }, []);

  return (
    <Panel
      onCloseButtonClick={() => {
        closeModal();
        publishModalAction('close_on_button_click');
      }}
      width={540}
      dataHook={AI_MODAL_ROOT}
      maxHeight={456}
    >
      <PanelHeader
        title={
          <Box gap="SP1">
            <Box color="B10">
              <SparklesFilled />
            </Box>
            <Heading size="medium">{t('AIPlugin_Modal_Title')}</Heading>
          </Box>
        }
      />

      {(() => {
        switch (state.type) {
          case 'INITIAL': {
            return selection ? (
              promptsList.length > 0 ? (
                <div className={styles.content}>
                  <PromptsList
                    ref={listRef}
                    visible={textualPrompt.length === 0}
                    prompts={promptsList}
                    highlightedIndex={state.selectedPromptIndex}
                    onHighlight={setSelectedPromptIndex}
                    onClick={prompt => {
                      submitPrompt(prompt, selection);
                      publishModalAction('prompt_selected', getPromptBiValue(prompt));
                    }}
                  />
                </div>
              ) : null
            ) : (
              <NoSelectionMessage
                title={initialPromptOptions?.emptyStateTitle}
                text={initialPromptOptions?.emptyStateText}
              />
            );
          }
          case 'IN_PROGRESS': {
            return (
              <div
                className={classNames(styles.content, styles.contentScrollReverse)}
                data-hook={AI_MODAL_LOADING_INDICATOR}
              >
                <Feed
                  entries={state.entries}
                  zIndex={resolvedZIndex}
                  getResultActions={getResultActions}
                  modalRef={{ current: containerEl }}
                  feedUserEntryFormatter={feedUserEntryFormatter}
                  isLoading
                />
              </div>
            );
          }
          case 'RESULTS': {
            return (
              <div
                className={classNames(styles.content, styles.contentScrollReverse)}
                data-hook={AI_MODAL_RESULTS_CONTENT}
              >
                <Feed
                  entries={state.entries}
                  zIndex={resolvedZIndex}
                  getResultActions={getResultActions}
                  modalRef={{ current: containerEl }}
                  feedUserEntryFormatter={feedUserEntryFormatter}
                />
              </div>
            );
          }
          case 'ERROR': {
            return (
              <div className={classNames(styles.content, styles.padded)}>
                <ErrorMessage />
              </div>
            );
          }
          default: {
            throw unreachable(state);
          }
        }
      })()}
      <SidePanel.Footer
        noPadding
        showDivider={
          (!(state.type === 'INITIAL' && promptsList.length === 0) && textualPrompt.length === 0) ||
          state.type !== 'INITIAL' ||
          !selection
        }
      >
        <Box
          paddingTop="SP3"
          paddingBottom="SP2"
          paddingLeft="SP4"
          paddingRight="SP4"
          align="right"
          className={styles.footer}
        >
          {(() => {
            switch (state.type) {
              case 'ERROR': {
                return (
                  <Box gap="SP2" paddingBottom="SP1">
                    <Button
                      dataHook={AI_MODAL_ERROR_CANCEL_BUTTON}
                      size="small"
                      priority="secondary"
                      onClick={() => {
                        goBack();
                        publishModalAction('back_from_error');
                      }}
                    >
                      {t('AIPlugin_Error_Button_GoBack')}
                    </Button>
                    <Button
                      dataHook={AI_MODAL_ERROR_RETRY_BUTTON}
                      size="small"
                      priority="primary"
                      onClick={() => {
                        retryPrompt();
                        publishModalAction('retry_from_error');
                      }}
                    >
                      {t('AIPlugin_Error_Button_Retry')}
                    </Button>
                  </Box>
                );
              }
              default: {
                return (
                  <Box direction="vertical" gap="SP2" width="100%">
                    <TextualPromptInputArea
                      busy={state.type === 'IN_PROGRESS'}
                      value={textualPrompt}
                      onChange={setFreePromptValue}
                      handleTextAreaEnter={state.type !== 'INITIAL'}
                      onSubmit={() => {
                        triggerFreePrompt();
                        publishModalAction('submit_free_prompt', textualPrompt);
                      }}
                      onCancel={() => {
                        cancelPrompt();
                        publishModalAction('cancel_free_prompt');
                      }}
                      ref={inputRef}
                      maxLength={PROMPT_CHAR_LIMIT}
                      zIndex={resolvedZIndex}
                      placeholder={
                        state.type === 'INITIAL' && initialPromptOptions?.placeholder
                          ? initialPromptOptions.placeholder
                          : undefined
                      }
                      isSubmitButtonVisible={initialPromptOptions?.isSubmitButtonVisible}
                    />
                    <Disclaimer />
                  </Box>
                );
              }
            }
          })()}
        </Box>
      </SidePanel.Footer>
    </Panel>
  );
};
